import { ClickAwayListener } from "@nova/component-click-away-listener"
import { ExtraSmallIcon, MagnifyingGlass, SmallIcon, XClose } from "@nova/component-icons"
import { useTranslation } from "@nova/use-translation"
import classNames from "classnames"
import { KeyboardEvent, useCallback, useEffect, useRef, useState } from "react"
import { animationClass } from "../../../../constants"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    selectFilteredTagIds,
    selectIsSearchingGlobally,
    selectSearchContents,
    setIsSearchingGlobally,
    setSearchContents,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { SearchTag } from "./SearchTag"
import { useActiveFolder } from "features/data-management/workspace/context/navigation"

export const WorkspaceSearchBar = () => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const inputRef = useRef<HTMLInputElement>(null)
    const activeFolder = useActiveFolder()

    const tagIds = useAppSelector(selectFilteredTagIds())

    const [isHovering, setIsHovering] = useState(false)
    const [isActive, setIsActive] = useState(false)
    const searchContents = useAppSelector(selectSearchContents()) || ""
    const isSearchingGlobally = useAppSelector(selectIsSearchingGlobally())

    const handleInputChange = useCallback(
        (input?: string) => {
            if ((input && input.length > 0) || !input) dispatch(setSearchContents(input))
        },
        [dispatch]
    )

    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === "Enter" && isActive) {
                setIsActive(false)
            } else if (e.key === "Escape" && isActive) {
                setIsActive(false)
                handleInputChange()
            }
        },
        [isActive, handleInputChange]
    )

    useEffect(() => {
        dispatch(setIsSearchingGlobally(tagIds.length === 0))
    }, [tagIds, dispatch])

    useEffect(() => {
        if (isActive) inputRef.current?.focus()
    }, [isActive])

    useEffect(() => {
        if (searchContents === undefined) {
            setIsActive(false)
            inputRef.current?.blur()
        }
    }, [searchContents])

    const hasContents = searchContents.length > 0
    const shouldShowTag = !isSearchingGlobally && (isActive || hasContents) && activeFolder?.name

    return (
        <ClickAwayListener
            className="flex-1 h-8 overflow-hidden max-w-[320px]"
            onClickAway={() => setIsActive(false)}
        >
            <div
                className={classNames(
                    "py-1.5 pl-2.5 flex gap-2 items-center rounded-full cursor-text",
                    animationClass,
                    {
                        "pr-3": !hasContents && !isActive,
                        "pr-1.5 bg-gray-500 text-gray-0": hasContents || isActive,
                        "bg-gray-900": !isActive && !hasContents,
                        "text-gray-300": !isHovering && !isActive && !hasContents,
                        "text-gray-100": isHovering && !isActive && !hasContents,
                        // "text-gray-0 bg-gray-500": isActive,
                    }
                )}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
                onClick={() => setIsActive(true)}
            >
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <SmallIcon
                        Icon={MagnifyingGlass}
                        className={classNames({
                            "text-blue-500": isActive || hasContents,
                        })}
                    />
                </div>
                {shouldShowTag && (
                    <SearchTag
                        title={activeFolder.name}
                        onClick={() => dispatch(setIsSearchingGlobally(true))}
                    />
                )}
                {!isActive ? (
                    <h5 className="flex-grow min-w-0 overflow-hidden whitespace-nowrap text-ellipsis select-none">
                        {t(hasContents ? searchContents : "Search for projects or folders")}
                    </h5>
                ) : (
                    <input
                        className={classNames(
                            "p-0 bg-transparent border-0 !shadow-none min-w-0 flex-grow",
                            "focus:outline-none focus:border-transparent focus:ring-0",
                            "text-sm" // <small> text style
                        )}
                        ref={inputRef}
                        type="text"
                        value={searchContents}
                        onChange={(e) => {
                            const sanitizedContents = e.target.value.trimStart()
                            handleInputChange(sanitizedContents)
                        }}
                        onKeyDown={handleKeyDown}
                    />
                )}
                {hasContents && (
                    <div
                        className={classNames(
                            "flex-shrink-0 rounded-full text-gray-100 cursor-pointer w-5 h-5 flex items-center justify-center",
                            "hover:bg-gray-700 hover:text-gray-0",
                            animationClass
                        )}
                        onClick={(e) => {
                            e.stopPropagation()
                            handleInputChange(undefined)
                        }}
                    >
                        <div className="absolute">
                            <ExtraSmallIcon Icon={XClose} />
                        </div>
                    </div>
                )}
            </div>
        </ClickAwayListener>
    )
}
